// 异步响应错误专用处理器

export interface ErrorContext {
  type: 'promise_rejection' | 'javascript_error' | 'network_error' | 'unknown';
  message: string;
  source?: string;
  timestamp: number;
  userAgent: string;
  url: string;
}

class ErrorHandler {
  private errorCounts = new Map<string, number>();
  private readonly MAX_SAME_ERROR_COUNT = 5;
  private readonly ERROR_TTL = 30000; // 30秒

  constructor() {
    this.setupHandlers();
  }

  private setupHandlers() {
    // 处理Promise拒绝
    window.addEventListener('unhandledrejection', (event) => {
      this.handlePromiseRejection(event);
    });

    // 处理JavaScript错误
    window.addEventListener('error', (event) => {
      this.handleJavaScriptError(event);
    });
  }

  private handlePromiseRejection(event: PromiseRejectionEvent) {
    const error = event.reason;
    const errorMessage = error?.message || String(error);
    
    // 检查是否为浏览器扩展相关的异步响应错误
    if (this.isBrowserExtensionError(errorMessage)) {
      this.handleBrowserExtensionError(errorMessage);
      event.preventDefault();
      return;
    }

    // 处理其他Promise错误
    this.logError({
      type: 'promise_rejection',
      message: errorMessage,
      source: error?.stack,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }

  private handleJavaScriptError(event: ErrorEvent) {
    const errorMessage = event.message;
    
    // 检查是否为浏览器扩展错误
    if (this.isBrowserExtensionError(errorMessage)) {
      this.handleBrowserExtensionError(errorMessage);
      event.preventDefault();
      return;
    }

    // 记录JavaScript错误（排除来自扩展的错误）
    if (event.filename && !this.isExtensionFile(event.filename)) {
      this.logError({
        type: 'javascript_error',
        message: errorMessage,
        source: `${event.filename}:${event.lineno}:${event.colno}`,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    }
  }

  private isBrowserExtensionError(message: string): boolean {
    const extensionErrorPatterns = [
      'message channel closed',
      'asynchronous response',
      'listener indicated',
      'before a response was received',
      'extension',
      'chrome-extension://',
      'moz-extension://'
    ];

    return extensionErrorPatterns.some(pattern => 
      message.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  private isExtensionFile(filename: string): boolean {
    return filename.includes('extension') || 
           filename.startsWith('chrome-extension://') || 
           filename.startsWith('moz-extension://');
  }

  private handleBrowserExtensionError(errorMessage: string) {
    // 限制相同错误的消息数量
    const errorKey = 'browser_extension_error';
    const currentCount = this.errorCounts.get(errorKey) || 0;
    
    if (currentCount < this.MAX_SAME_ERROR_COUNT) {
      console.warn('🔇 浏览器扩展异步响应错误（已忽略）:', errorMessage);
      this.errorCounts.set(errorKey, currentCount + 1);
      
      // 清理过期的错误计数
      setTimeout(() => {
        const count = this.errorCounts.get(errorKey) || 0;
        if (count <= 1) {
          this.errorCounts.delete(errorKey);
        } else {
          this.errorCounts.set(errorKey, count - 1);
        }
      }, this.ERROR_TTL);
    }
  }

  private logError(context: ErrorContext) {
    const errorKey = `${context.type}:${context.message.substring(0, 50)}`;
    const currentCount = this.errorCounts.get(errorKey) || 0;
    
    // 防止错误刷屏
    if (currentCount >= this.MAX_SAME_ERROR_COUNT) {
      return;
    }

    this.errorCounts.set(errorKey, currentCount + 1);

    console.group(`❌ ${context.type.toUpperCase()} ERROR #${currentCount + 1}`);
    console.error('Message:', context.message);
    console.error('Source:', context.source);
    console.error('Timestamp:', new Date(context.timestamp).toISOString());
    console.error('URL:', context.url);
    console.groupEnd();

    // 清理计数
    setTimeout(() => {
      const count = this.errorCounts.get(errorKey) || 0;
      if (count <= 1) {
        this.errorCounts.delete(errorKey);
      } else {
        this.errorCounts.set(errorKey, count - 1);
      }
    }, this.ERROR_TTL);
  }

  // 公共API：手动报告错误
  public reportError(message: string, source?: string) {
    this.logError({
      type: 'unknown',
      message,
      source,
      timestamp: Date.now(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }

  // 获取错误统计
  public getErrorStats(): { [key: string]: number } {
    const stats: { [key: string]: number } = {};
    this.errorCounts.forEach((count, key) => {
      stats[key] = count;
    });
    return stats;
  }
}

// 导出单例实例
export const errorHandler = new ErrorHandler();

// 开发模式下暴露到全局
if (process.env.NODE_ENV === 'development') {
  (window as any).errorHandler = errorHandler;
}